/**
 * FinanceApprovalQueuePage — Phase 35.9.
 *
 * Unified founder review queue that aggregates three F-3 approval surfaces:
 *   - Pending financial reports (daily / weekly / monthly)
 *   - Pending VAT / withholding tax submissions (last 3 months)
 *   - Pending period-close approvals
 *
 * Items >48 h old get a red urgency border. Sort: oldest-first.
 */

import { useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

export type ApprovalItemType = 'report' | 'vat' | 'withholding' | 'period-close';

export interface FinanceQueueItem {
  item_id: string;
  item_type: ApprovalItemType;
  title_he: string;
  period: string;
  queued_at_iso: string;
  is_urgent: boolean;
  status: string;
  amount_agorot?: number;
}

interface ReportSummary {
  report_id: string;
  report_type: string;
  period_start: string;
  period_end: string;
  generated_at: string;
  approved: boolean;
}

interface TaxCalc {
  calc_id: string;
  tax_type: string;
  period: string;
  amount_agorot: number;
  status: string;
  founder_approved_at: string | null;
}

interface PeriodClose {
  close_id: string;
  period_type: string;
  period: string;
  status: string;
  closed_at: string | null;
}

// ── Helpers (exported for tests) ─────────────────────────────────────────────

export const ITEM_TYPE_LABELS: Record<ApprovalItemType, string> = {
  report:        'Report',
  vat:           'VAT',
  withholding:   'Withholding tax',
  'period-close': 'Period closing',
};

export function itemTypeLabel(t: ApprovalItemType): string {
  return ITEM_TYPE_LABELS[t] ?? t;
}

export function queueItemStatusColor(s: string): string {
  switch (s) {
    case 'pending':  return '#fbbf24';
    case 'approved': return '#34d399';
    case 'open':     return '#7dd3fc';
    case 'closed':   return '#a78bfa';
    default: return 'var(--omega-ink-subtle)';
  }
}

export function isUrgent(queuedAtIso: string): boolean {
  return Date.now() - new Date(queuedAtIso).getTime() > 48 * 3600 * 1000;
}

export function recentPeriods(n: number): string[] {
  const now = new Date();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
}

export function fmtQueuedAt(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function reportTypeHe(t: string): string {
  switch (t) {
    case 'daily':   return 'Daily';
    case 'weekly':  return 'Weekly';
    case 'monthly': return 'Monthly';
    default: return t;
  }
}

// Normalize API responses to a common queue item shape.
export function normalizeReport(r: ReportSummary): FinanceQueueItem {
  return {
    item_id:      r.report_id,
    item_type:    'report',
    title_he:     `Report ${reportTypeHe(r.report_type)} — ${r.period_start.slice(0, 7)}`,
    period:       r.period_start.slice(0, 7),
    queued_at_iso: r.generated_at,
    is_urgent:    isUrgent(r.generated_at),
    status:       r.approved ? 'approved' : 'pending',
  };
}

export function normalizeTax(t: TaxCalc): FinanceQueueItem {
  const taxLabel = t.tax_type === 'vat' ? 'VAT' : 'Withholding tax';
  return {
    item_id:       t.period,
    item_type:     t.tax_type === 'vat' ? 'vat' : 'withholding',
    title_he:      `${taxLabel} — ${t.period}`,
    period:        t.period,
    queued_at_iso: `${t.period}-01T00:00:00Z`,
    is_urgent:     isUrgent(`${t.period}-01T00:00:00Z`),
    status:        t.status,
    amount_agorot: t.amount_agorot,
  };
}

export function normalizePeriodClose(c: PeriodClose): FinanceQueueItem {
  const queuedAt = c.closed_at ?? `${c.period}-01T00:00:00Z`;
  return {
    item_id:       c.close_id,
    item_type:     'period-close',
    title_he:      `Period closing — ${c.period}`,
    period:        c.period,
    queued_at_iso: queuedAt,
    is_urgent:     isUrgent(queuedAt),
    status:        c.status,
  };
}

// ── Page ─────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
};

export function FinanceApprovalQueuePage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [typeFilter, setTypeFilter] = useState<'' | ApprovalItemType>('');
  const periods = recentPeriods(3);

  const reportsQuery = useQuery({
    queryKey: ['finance', 'approval-queue', 'reports'],
    queryFn: () => apiFetch<{ reports: ReportSummary[]; total: number }>('/finance/reports'),
    refetchInterval: 30_000,
  });

  const closesQuery = useQuery({
    queryKey: ['finance', 'approval-queue', 'closes'],
    queryFn: () => apiFetch<{ closes: PeriodClose[]; total: number }>('/finance/period-closes'),
    refetchInterval: 30_000,
  });

  // Fetch VAT + withholding for last 3 periods; treat 404 as no data.
  const vatQueries = periods.map(p => useQuery({  // eslint-disable-line react-hooks/rules-of-hooks
    queryKey: ['finance', 'approval-queue', 'vat', p],
    queryFn: () => apiFetch<TaxCalc>(`/finance/tax/vat?period=${p}`).catch(() => null),
    refetchInterval: 30_000,
  }));

  const withholdingQueries = periods.map(p => useQuery({  // eslint-disable-line react-hooks/rules-of-hooks
    queryKey: ['finance', 'approval-queue', 'withholding', p],
    queryFn: () => apiFetch<TaxCalc>(`/finance/tax/withholding?period=${p}`).catch(() => null),
    refetchInterval: 30_000,
  }));

  const isPending = reportsQuery.isPending || closesQuery.isPending;
  const isError   = reportsQuery.isError   || closesQuery.isError;

  const items = useMemo<FinanceQueueItem[]>(() => {
    const all: FinanceQueueItem[] = [];

    for (const r of reportsQuery.data?.reports ?? []) {
      if (!r.approved) all.push(normalizeReport(r));
    }
    for (const c of closesQuery.data?.closes ?? []) {
      if (c.status !== 'approved') all.push(normalizePeriodClose(c));
    }
    for (const q of [...vatQueries, ...withholdingQueries]) {
      const t = q.data;
      if (t && t.status !== 'approved' && t.status !== 'filed') all.push(normalizeTax(t));
    }

    const filtered = typeFilter ? all.filter(i => i.item_type === typeFilter) : all;
    return filtered.sort((a, b) =>
      new Date(a.queued_at_iso).getTime() - new Date(b.queued_at_iso).getTime(),
    );
  }, [reportsQuery.data, closesQuery.data, vatQueries, withholdingQueries, typeFilter]);

  const urgentCount = items.filter(i => i.is_urgent && i.status === 'pending').length;

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',   to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Approval queue' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', direction: 'ltr' }}>
          Financial approval queue
        </h1>
        {urgentCount > 0 && (
          <span style={{
            background: '#f87171', color: '#0a0d12', fontSize: '8pt', fontWeight: 700,
            minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {urgentCount}
          </span>
        )}
        {!isPending && (
          <span style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)' }}>
            {items.length}
          </span>
        )}
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value as '' | ApprovalItemType)}
          style={inputStyle}
        >
          <option value="">All</option>
          <option value="report">Reports</option>
          <option value="vat">VAT</option>
          <option value="withholding">Withholding tax</option>
          <option value="period-close">Period closings</option>
        </select>
      </div>

      {/* Column headers */}
      {!isPending && !isError && items.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '90px 1fr 90px 130px 80px 72px',
          gap: 10, padding: '6px 12px',
          borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
          marginBottom: 2,
        }}>
          {['Type', 'Title', 'Period', 'Date', 'Status', 'Action'].map(h => (
            <span key={h} style={{ fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--omega-ink-subtle)', direction: 'ltr' }}>
              {h}
            </span>
          ))}
        </div>
      )}

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading queue.</div>}

      {!isPending && !isError && items.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '40px 0', textAlign: 'center', direction: 'ltr' }}>
          No items pending approval — all handled!
        </div>
      )}

      {items.map(item => (
        <div
          key={`${item.item_type}-${item.item_id}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '90px 1fr 90px 130px 80px 72px',
            gap: 10, alignItems: 'center',
            padding: '10px 12px',
            borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
            borderLeft: item.is_urgent && item.status === 'pending'
              ? '3px solid #f87171' : '3px solid transparent',
          }}
        >
          <span style={{
            fontSize: '7.5pt', padding: '1px 6px',
            border: '1px solid rgba(255,255,255,0.15)',
            color: 'var(--omega-ink-subtle)', borderRadius: 3,
            textAlign: 'center', direction: 'ltr',
          }}>
            {itemTypeLabel(item.item_type)}
          </span>

          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: '9.5pt', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', direction: 'ltr' }}>
              {item.is_urgent && item.status === 'pending' && (
                <span style={{ color: '#f87171', marginLeft: 6, fontSize: '8pt' }}>⚠ Urgent</span>
              )}
              {item.title_he}
            </div>
          </div>

          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
            {item.period}
          </span>

          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
            {fmtQueuedAt(item.queued_at_iso)}
          </span>

          <span style={{
            fontSize: '7.5pt', padding: '1px 6px',
            border: `1px solid ${queueItemStatusColor(item.status)}`,
            color: queueItemStatusColor(item.status),
            letterSpacing: '0.06em', textTransform: 'uppercase',
            fontWeight: 700, borderRadius: 3, justifySelf: 'start',
          }}>
            {item.status}
          </span>

          <Link
            to={`/omega/finance/approve/${item.item_type}/${encodeURIComponent(item.item_id)}`}
            style={{
              background: 'rgba(251,191,36,0.12)',
              border: '1px solid rgba(251,191,36,0.35)',
              borderRadius: 4, color: '#fbbf24',
              fontFamily: 'var(--omega-mono)', fontSize: '8pt',
              fontWeight: 600, padding: '4px 10px',
              textDecoration: 'none', direction: 'ltr', whiteSpace: 'nowrap',
            }}
          >
            Review
          </Link>
        </div>
      ))}
    </div>
  );
}
